import { Button, Dropdown, MenuProps, Table, Tooltip, Tree } from 'antd'
import { TFunction } from 'i18next';
import React from 'react'
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hook';
import classes from './Agreement.module.scss';
import { IColumn, ISimpleGroupForAprove, ISimpleUserForAprove } from 'interfaces';
import { InviteStatusEnum } from 'interfaces/IInvitations';
import { CheckOutlined, CloseOutlined, DownloadOutlined, EditOutlined, EllipsisOutlined, PrinterOutlined, UserOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHourglass } from '@fortawesome/free-solid-svg-icons';
import { defaultGroups } from 'store/reducers/usersGroupCreator';
import { pdfHelper } from 'helpers/pdfHelper';
import dayjs from 'dayjs';
import cn from 'classnames';
import RowLoader from 'components/Sceletons/RowLoader';
import api from 'api';
import agreement from 'api/agreement';
import getDateWithTimeZone from 'helpers/getDateWithTimeZone';
import { prepareGroups } from './AgreementCard';
import { fetchAgreements } from 'store/reducers/agreementsCreator';

interface IUserAgreement extends ISimpleUserForAprove {
  user_group: string;
  key: string
}

const getLocaleColumns = (t: TFunction) => [
  {
    title: t('Documents.details.agreement.user'),
    dataIndex: 'user_name',
    key: 'user_name',
    width: '40%',
  },
  {
    title: t('Documents.details.agreement.updated_at'),
    dataIndex: 'updated_at',
    key: 'updated_at',
    width: '40%',
  },
  {
    title: t('Documents.details.agreement.state'),
    dataIndex: 'agreement_state',
    key: 'agreement_state',
    width: '20%',
    className: classes.textToCenter,
  },
];

export enum IIconCases { Null = 'null', False = 'false', True = 'true' };
export const iconCases = (type: IIconCases, size?: 'small' | 'large') => {
  const sizeClass = size === 'small' ? classes.smallIcon : classes.largeIcon; 
  const icons = {
    [IIconCases.Null]: <FontAwesomeIcon className={cn(classes.glassWaiting, sizeClass)} icon={faHourglass} />,
    [IIconCases.False]: <CloseOutlined className={cn(classes.closeRed, sizeClass)} />,
    [IIconCases.True]: <CheckOutlined className={cn(classes.checkGreen, sizeClass)} />
  };
  return icons[type];
};

type AgreementUsersPorps = {
  usersWithPermissions: any[];
  loadingUsersPermissions: boolean;
}

export default function AgreementUsers({ usersWithPermissions, loadingUsersPermissions }: AgreementUsersPorps) {
  
  const [modifiedColumns, setModifiedColumns] = React.useState<IColumn[]>([]);
  const [isOpenDropdown, setIsOpenDropdown] = React.useState(false);
  const [downloadProcess, setDownloadProcess] = React.useState<string[]>([]);
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [addMode, setAddMode] = React.useState(false);
  const [deleteMode, setDeleteMode] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isAddingParticipants, setIsAddingParticipants] = React.useState(false);
  const [selectedUserIds, setSelectedUserIds] = React.useState<string[]>([]);
  const [usersIdToAdd, setUsersIdToAdd] = React.useState<string[]>([]);
  const [dataTreeSelect, setDataTreeSelect] = React.useState<any[]>([]);  

  const { isArgeementsLoading, agreements, agreementInfo } = useAppSelector((store) => store.agreements);
  const { users, groups, isGroupsAndUsersLoading, pendingInviteUsers } = useAppSelector((store) => store.dataUsers);
  const { selectedDocument, selectedFolder } = useAppSelector((store) => store.documents);
  const { dataRoom } = useAppSelector((store) => store.dataRoom);
  const { userData } = useAppSelector((store) => store.userData);
  const { messageApi } = useAppSelector((store) => store.windowState);
  const dispatch = useAppDispatch();
  
  const logoTitle = window.REACT_APP_BUILD_NAME === 'athena' ? 'Athena' : 'TazaDeal'

  const { t, i18n } = useTranslation();

  const isInitiator = userData?.id === agreementInfo?.initiator?.id;
  const currentAgreementStatus = selectedDocument ? selectedDocument.agreement_status : selectedFolder?.agreement_status;
  const isAgreementCompleted = agreementInfo?.status === 1 || agreementInfo?.status === 2 || currentAgreementStatus === 1 || currentAgreementStatus === 2;
  const canEdit = isInitiator && !isAgreementCompleted;

  const handleCheckToAdd = (checkedKeys: any) => {
    const keysArray: string[] = Array.isArray(checkedKeys) ? checkedKeys : checkedKeys.checked;
    const groupIds = new Set(groups.map(g => g.id));
    const selectedUserIds = keysArray.filter(key => !groupIds.has(key));
    setUsersIdToAdd(selectedUserIds);
  };

  React.useEffect(() => {
    if (isAgreementCompleted) {
      setIsEditMode(false);
      setAddMode(false);
      setDeleteMode(false);
      setSelectedUserIds([]);
    }
  }, [isAgreementCompleted]);

  React.useEffect(() => {
    const rightUsers = users.map(user => ({
      ...user,
      document_permissions: usersWithPermissions
        ? usersWithPermissions.find(item => item.email === user.email)?.document_permissions
        : usersWithPermissions
    }));
    const rightUsersIds = new Set(rightUsers.map(u => u.id));

    const invitesAsUsers = pendingInviteUsers
      .filter(invite => invite.invite_status === InviteStatusEnum.PENDING && !invite.is_parent_invite && !rightUsersIds.has(invite.id))
      .map(invite => ({
        id: invite.user_id,
        first_name: '',
        last_name: '',
        email: invite.email,
        phone_number: '',
        group: [invite.group_id],
        type: 'invite'
      }));

    const existingParticipantIds = new Set(
      (usersWithGroups ?? []).map((u: IUserAgreement) => u.user_id)
    );

    const treeData = groups.map(group => {
      const node = prepareGroups(group, [...rightUsers, ...invitesAsUsers], t, 'can_edit_agreement');
      return {
        ...node,
        children: node.children.map((child: any) => {
          if (!existingParticipantIds.has(child.value)) return child;
          const iconKey = `${true}` as IIconCases;
          return {
            ...child,
            disableCheckbox: true,
            title: (
              <div className={classes.existingParticipant}>
                {iconCases(iconKey, 'small')}
                {child.title}
              </div>
            ),
          };
        }),
      };
    });
    setDataTreeSelect(treeData);
  }, [users, usersWithPermissions, pendingInviteUsers, i18n.language, agreements]);

  const handleAddParticipants = async () => {
    if (!usersIdToAdd.length || !agreementInfo?.id) return;
    const allUsers = [
      ...users.map(user => ({ ...user, type: 'user' })),
      ...pendingInviteUsers
        .filter(invite => invite.invite_status === InviteStatusEnum.PENDING)
        .map(invite => ({
          id: invite.user_id,
          first_name: '',
          last_name: '',
          email: invite.email,
          phone_number: '',
          group: [invite.group_id],
          type: 'invite'
        }))
    ];

    const choosenUsers = allUsers.filter(user => usersIdToAdd.includes(user.id)).map(user => ({
      user_id: user.id,
      user_name: user.type === 'invite' ? user.email : user.first_name,
      user_email: user.email,
      agreement_state: null,
      parent_group_id: user.group[0],
    }));

    const simpleGroups: ISimpleGroupForAprove[] = groups.map(group => ({group_id: group.id, group_name: group.name}));

    const jsonForRequest = choosenUsers.reduce((acc: ISimpleGroupForAprove[], currentUser: ISimpleUserForAprove) => {
      const targetGroup = simpleGroups.find(group => group.group_id === currentUser.parent_group_id);
      const theSameGroupInAcc = acc.find((group => group.group_id === targetGroup?.group_id));

      delete currentUser.parent_group_id;

      theSameGroupInAcc
        ? acc = acc.map((group: any) => (group.group_id === targetGroup?.group_id ? {...group, users: [...group.users, currentUser]} : group))
        : acc.push({...targetGroup!, users: [currentUser]});

      return acc;
    }, []);

    setIsAddingParticipants(true);
    try {
      await agreement.addAgreementParticipants({
        entity_id: selectedDocument ? selectedDocument.id : selectedFolder?.id!,
        entity: selectedDocument ? 'document' : 'folder',
        agreement_state: jsonForRequest,
      });
      messageApi?.success(t('Documents.participants.addSuccess'));
      selectedDocument
        ? dispatch(fetchAgreements({ id: selectedDocument.id, entity: 'document' }))
        : dispatch(fetchAgreements({ id: selectedFolder?.id!, entity: 'folder' }));
    } catch {
      messageApi?.error(t('Documents.participants.addError'));
    } finally {
      setIsAddingParticipants(false);
      setAddMode(false);
      setSelectedUserIds([]);
      setIsEditMode(false);
    }
  };

  const handleToggleEditMode = () => {
    setIsEditMode((prev) => !prev );
    setAddMode(false);
    setDeleteMode(false);
    setSelectedUserIds([]);
  };

  const handleDeleteParticipants = async () => {
    if (!selectedUserIds.length || !agreementInfo?.id) return;
    setIsDeleting(true);
    try {
      await agreement.deleteAgreementParticipants({
        entity_id: selectedDocument ? selectedDocument.id : selectedFolder?.id!,
        entity: selectedDocument ? 'document' : 'folder',
        user_ids: selectedUserIds,
      });
      messageApi?.success(t('Documents.participants.deleteSuccess'));
      selectedDocument
        ? dispatch(fetchAgreements({ id: selectedDocument.id, entity: 'document' }))
        : dispatch(fetchAgreements({ id: selectedFolder?.id!, entity: 'folder' }));
    } catch {
      messageApi?.error(t('Documents.participants.deleteError'));
    } finally {
      setIsDeleting(false);
      setSelectedUserIds([]);
      setDeleteMode(false);
      setIsEditMode(false);
    }
  };

  const usersWithGroups = agreements?.reduce((acc: IUserAgreement[], currentGroup: ISimpleGroupForAprove) => {
    if (currentGroup.users) {
      const usersWithGroupAndFullName = currentGroup.users.map(user => {
        const registeredUser = users.find(curr => curr.email === user.user_email);
        const inviteUser = pendingInviteUsers.find(curr => curr.email === user.user_email);
        
        const displayName = inviteUser 
          ? `${inviteUser.email}`
          : (registeredUser && registeredUser.first_name && registeredUser.last_name && registeredUser.email)
            ? `${registeredUser.first_name} ${registeredUser.last_name} - ${registeredUser.email}`
            : `${t('Users.table.invitedUser')} - ${user.user_email}`;
          
        return {
          ...user,
          user_group: currentGroup.group_name,
          user_name: displayName,
          key: user.user_id,
        }
      })
      acc = [...acc, ...usersWithGroupAndFullName]
    }
    return acc
  }, [])

  React.useEffect(()=> {
    const tableColumns = getLocaleColumns(t);
    const customColumns = tableColumns.map((col: IColumn) => ({
      ...col, 
      render: (value: string, info: ISimpleUserForAprove) => renderCell({value, info, col}),
    }))
    setModifiedColumns(customColumns);
  },[isGroupsAndUsersLoading, isArgeementsLoading, i18n.language]);

  const getLocaleDate = (timestamp: string) => {
    const inSeconds = dayjs(timestamp).valueOf();
    return timestamp ? getDateWithTimeZone(inSeconds, userData?.date_format!) : '-'
  }

  const renderCell = (props: any) => {
    const { value, info, col } = props;
    
    if(col.key === 'agreement_state'){ 
      const iconKey = `${value}` as IIconCases;      
      return iconCases(iconKey);
    }

    if(col.key === 'user_group'){
      return defaultGroups.includes(value)
        ? t(`Users.table.${value}`)
        : value
    }
    if(col.key === 'updated_at'){ 
      return getLocaleDate(value);
    }
   
    return value;
  };

  const createExportLog = async (action_type: string, log_type: string) => {
    try {
      await api.logExportAgreementState({room_id: dataRoom?.id!, action_type, log_type, entity_id: selectedDocument!.id})
    } catch (e) {
      console.log(e);
    };
  }

  const getReport = async (type: string) => {
    setDownloadProcess(prev => [...prev, type]);
    // api.logPrint(dataRoom?.id!, false);

    const statusLocalized = {
      true: t('Documents.details.agreement.userAgreed'),
      false: t('Documents.details.agreement.userDeclinedAgreemnet'),
      null: t('Documents.details.agreement.userNotAgreed'),
    };
    
    try {
      const tableColumns = getLocaleColumns(t);
      const usersWithGroupsWithTextStatus = usersWithGroups.map(row => ({
        ...row,
        agreement_state: statusLocalized[String(row.agreement_state) as keyof typeof statusLocalized],
        updated_at: row.updated_at ? getLocaleDate(String(row.updated_at)) : '-',
        user_group: defaultGroups.includes(row.user_group) ? t(`Users.table.${row.user_group}`) : row.user_group
      }));
      const currentTime =  dayjs(Date.now()).format('HH:mm DD-MM-YYYY');
      const currentTimeInUTC = dayjs(Date.now()).utc().format('HH:mm DD-MM-YYYY');
      const offset = - new Date().getTimezoneOffset()/60;
      const nameOfTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const slicedWord = selectedDocument?.name.slice(-selectedDocument?.name.length + 55, -12) || '';      
      const nameWithNeededLength = selectedDocument?.name && selectedDocument?.name.length > 80 
        ? selectedDocument?.name.replace(slicedWord, '/.../') 
        : selectedDocument?.name;

      const titleText = [
        `${t('Documents.details.agreement.agreementTitle')} - ${nameWithNeededLength}`,
        `${logoTitle} - ${currentTime} ${nameOfTimeZone}UTC+${offset} (${currentTimeInUTC}UTC+0)`
      ]

      pdfHelper<IUserAgreement>(
        usersWithGroupsWithTextStatus,
        tableColumns,
        titleText,
        type
      )
      await createExportLog(type, 'approval')
    } catch (e) {
      messageApi?.error(t(`Documents.details.agreement.${type}Error`));
    } finally {
      setDownloadProcess(downloadProcess.filter(el => el === type));
    }
  };

  const rowSelection = deleteMode ? {
    selectedRowKeys: selectedUserIds,
    onChange: (keys: React.Key[]) => setSelectedUserIds(keys as string[]),
    getCheckboxProps: (record: IUserAgreement) => ({
      disabled: record.agreement_state !== null && record.agreement_state !== undefined,
    }),
  } : undefined;

  const actionItems: MenuProps['items'] = [
    ...(canEdit ? [{
      key: 'edit',
      className: classes.liItem,
      label: (
        <Button
          onClick={handleToggleEditMode}
          icon={<EditOutlined />}
          className={classes.liButton}
        >
          {isEditMode ? t('Documents.participants.cancel') : t('Documents.participants.edit')}
        </Button>
      ),
    }] : []),
    {
      key: 'export',
      className: classes.liItem,
      label: (
        <Button
          loading={downloadProcess.includes('export')}
          onClick={() => getReport('export')}
          disabled={Boolean(downloadProcess[0])}
          icon={<DownloadOutlined />}
          className={classes.liButton}
        >
          {t('Confirm.download')}
        </Button>
      ),
    },
    {
      key: 'print',
      className: classes.liItem,
      label: (
        <Button
          loading={downloadProcess.includes('print')}
          onClick={() => getReport('print')}
          disabled={Boolean(downloadProcess[0])}
          icon={<PrinterOutlined />}
          className={classes.liButton}
        >
          {t('Confirm.print')}
        </Button>
      ),
    },
  ];

  return (
    <div className={classes.table}>
      <div className={classes.tableTitleWrap}>
        <span className={classes.title}>
          {t('Documents.details.agreement.agreementState')}
        </span>
        <Dropdown
          open={isOpenDropdown || Boolean(downloadProcess[0])}
          onOpenChange={(open) => setIsOpenDropdown(open)}
          menu={{ items: actionItems }}
          placement='bottomRight'
        >
          <Button icon={<EllipsisOutlined />} />
        </Dropdown>
      </div>

      {agreementInfo?.initiator && (
        <div className={classes.initiatorBlock}>
          <UserOutlined className={classes.initiatorIcon} />
          <div className={classes.initiatorInfo}>
            <span className={classes.initiatorName}>
              {agreementInfo.initiator.user_name}
            </span>
            <span className={classes.initiatorEmail}>
              {agreementInfo.initiator.email}
            </span>
          </div>
          <span className={classes.initiatorBadge}>
            {t('Documents.participants.initiator')}
          </span>
        </div>
      )}

      {isEditMode && (
        <div className={classes.editActions}>
          {!deleteMode && (
            <Button
              type='primary'
              className={classes.editButton}
              loading={isAddingParticipants}
              disabled={addMode && !usersIdToAdd.length}
              onClick={() => {
                if (addMode) {
                  handleAddParticipants();
                } else {
                  setAddMode(true);
                  setDeleteMode(false);
                }
              }}
            >
              {t('Documents.participants.addUser')}
            </Button>
          )}
          {!addMode && (
            <Tooltip title={selectedUserIds?.length === usersWithGroups?.length && t('Documents.participants.deletingImpossible')}>
              <Button
                variant='solid'
                color='danger'
                className={classes.editButton}
                disabled={(deleteMode && !selectedUserIds.length) || selectedUserIds?.length === usersWithGroups?.length}
                loading={isDeleting}
                onClick={() => {
                  if (deleteMode) {
                    handleDeleteParticipants();
                  } else {
                    setDeleteMode(true);
                    setAddMode(false);
                  }
                }}
              >
                {t('Documents.participants.deleteUser')}
              </Button>
            </Tooltip>
          )}
        </div>
      )}

      {addMode && (
        isGroupsAndUsersLoading || loadingUsersPermissions ? (
          <RowLoader height={180} width={250} padding={'0 0 0 0'} />
        ) : (
          <Tree
            checkable
            disabled={isAddingParticipants}
            treeData={dataTreeSelect}
            checkedKeys={usersIdToAdd}
            onCheck={handleCheckToAdd}
            className={classes.addParticipantsTree}
          />
        )
      )}

      {!addMode && agreements[0] && (
        <Table
          columns={modifiedColumns}
          dataSource={usersWithGroups}
          pagination={false}
          size='small'
          loading={isArgeementsLoading || isGroupsAndUsersLoading}
          rowSelection={rowSelection}
          rowKey='key'
        />
      )}
    </div>
  );
}
